import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createId } from '@paralleldrive/cuid2';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Popover,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';
import {
  GET_ALL_FILTER_TYPES_QUERY,
  GET_MY_SITES_QUERY,
  GET_SITE_FINDS_QUERY,
} from '../../gql';
import { Logo } from '../../components/Logo';
import { FilterBuilder } from '../../components/FindToolbox/Filters/FilterBuilder';
import {
  VisualizationDefinition,
  VisualizationDefinitionFilter,
  VisualizationPreset,
  VisualizationShape,
  VisualizationStyle,
  VisualizationSymbol,
  VisualizerMapView,
  normalizeVisualizerFind,
} from '../../components/VisualizerMapView';
import {
  VisualizationDefinitionSummary,
  VisualizationPresetDialog,
} from '../../components/VisualizationPresetDialog';
import { FindPresetDialogMode } from '../../components/FindPresetDialog';
import { useUnsavedChangesGuard } from '../../components/context';
import { useSettings } from '../../components/context/SettingsContext/useSettings';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  FieldLabel,
  FormField,
  Button as DesignSystemButton,
  IconButton as DesignSystemIconButton,
  SurfaceAutocomplete,
  SurfaceTextField,
  SwitchLabel,
  Switch,
} from '../../components/design-system';

interface SiteOption {
  id: string | null;
  name: string;
  longitude?: number | null;
  latitude?: number | null;
}

const NONE_SITE: SiteOption = { id: null, name: 'None' };
const SHAPES: VisualizationShape[] = [
  'circle',
  'square',
  'triangle',
  'rhombus',
  'star',
];
const DEFAULT_STYLE: VisualizationStyle = {
  shape: 'circle',
  primaryColor: '#e32e2e',
  secondaryColor: null,
  sizeScale: 1,
  outlined: true,
};
const EMPTY_FILTER: VisualizationDefinitionFilter = {};
const headerStyles = {
  px: { xs: 2, md: 3 },
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const homeButtonStyles = {
  '& img': {
    transform: 'translateY(-1px) scale(1.45)',
  },
};
const pageStyles = { minHeight: '100%', bgcolor: 'background.default' };
const layoutStyles = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(420px, 1fr) 1fr' },
  gap: 2,
  p: { xs: 1.5, md: 2 },
};
const editorStyles = { p: 2, minWidth: 0 };
const mapStyles = {
  position: 'relative',
  minHeight: { xs: 420, lg: 600 },
  height: '100%',
  overflow: 'hidden',
};
const mapLoadingStyles = {
  position: 'absolute',
  inset: 0,
  zIndex: 5,
  pointerEvents: 'none',
  bgcolor: 'rgba(0, 0, 0, 0.18)',
};
const tableStyles = {
  gridColumn: { xs: '1', lg: '1 / -1' },
  minWidth: 0,
};
const definitionTableStyles = { width: '100%', tableLayout: 'fixed' };
const definitionFilterCellStyles = { minWidth: 0, overflow: 'hidden' };
const definitionIconCellStyles = { width: 64, textAlign: 'center' };
const shapeGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 64px)',
  gap: 0.5,
  p: 0.75,
  maxHeight: 260,
  overflowY: 'auto',
};
const shapeOptionStyles = {
  borderRadius: 1,
  minHeight: 58,
  color: 'text.secondary',
  '&:hover': {
    bgcolor: 'action.hover',
    color: 'common.black',
  },
};
const selectedShapeOptionStyles = {
  ...shapeOptionStyles,
  bgcolor: 'action.selected',
  color: 'common.black',
};
const nativeColorStyles = (theme: Theme) => ({
  width: 26,
  height: 26,
  border: 0,
  borderRadius: '50%',
  padding: 0,
  background: 'transparent',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 1px 5px 1px rgba(0, 0, 0, 0.26)'
      : '0 1px 4px 1px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  overflow: 'hidden',
  '&::-webkit-color-swatch-wrapper': { padding: 0 },
  '&::-webkit-color-swatch': { border: 0, borderRadius: '50%' },
  '&::-moz-color-swatch': { border: 0, borderRadius: '50%' },
});
const definitionRowStyles = {
  cursor: 'pointer',
  '&.Mui-selected': { bgcolor: 'action.selected' },
  '&.Mui-selected:hover': { bgcolor: 'action.selected' },
};
const compactRowDirection = { xs: 'column', sm: 'row' } as const;
const responsiveControlAlignItems = {
  xs: 'stretch',
  sm: 'flex-end',
} as const;
const nameInputProps = { maxLength: 100 };
const colorInputProps = { maxLength: 7 };
const staticLabelStyles = {
  color: 'text.secondary',
  fontSize: 12,
  lineHeight: 1.5,
};
const neutralInputStyles = {
  '& .MuiOutlinedInput-root': {
    minHeight: 34,
    bgcolor: 'sunken',
    fontSize: 13,
    '& fieldset': { borderColor: 'outline' },
    '&:hover fieldset': { borderColor: 'text.secondary' },
    '&.Mui-focused fieldset': {
      borderColor: 'text.primary',
      borderWidth: 1,
    },
  },
};
const primaryColorFieldStyles = {
  ...neutralInputStyles,
  width: 88,
  '& .MuiOutlinedInput-input': { px: 1, py: 0.75 },
};
const secondaryColorFieldStyles = {
  ...primaryColorFieldStyles,
  width: 88,
};
const fieldGroupStyles = { flex: 1, minWidth: 180 };
const presetActionStyles = {
  minHeight: 34,
  color: 'text.primary',
  borderColor: 'outline',
  '&:hover': {
    bgcolor: 'action.hover',
    borderColor: 'text.secondary',
  },
};
const presetActionsStyles = { alignItems: 'center' };
const playgroundButtonStyles = { ml: 'auto' };
const neutralIconButtonStyles = {
  color: 'text.secondary',
  width: 30,
  height: 30,
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const neutralSliderStyles = {
  width: 112,
  color: 'text.secondary',
  py: 0.75,
  '& .MuiSlider-thumb': {
    width: 12,
    height: 12,
    bgcolor: 'text.primary',
    boxShadow: 'none',
  },
  '& .MuiSlider-track': { border: 0 },
  '& .MuiSlider-rail': { opacity: 0.35 },
};
const styleControlRowStyles = {
  alignItems: 'center',
  columnGap: 2,
  rowGap: 1,
};
const colorControlStyles = {
  minWidth: 150,
  alignItems: 'center',
  columnGap: 0.75,
};
const colorValueRowStyles = { minHeight: 34 };
const sizeControlStyles = { width: 145, alignItems: 'center' };
const sizeValueStyles = {
  minWidth: 30,
  color: 'text.secondary',
  fontSize: 12,
  textAlign: 'right',
};
const shapeButtonStyles = {
  minHeight: 34,
  px: 1,
  color: 'text.primary',
  justifyContent: 'flex-start',
  '&:hover': {
    bgcolor: 'action.hover',
    color: 'common.black',
  },
};
const activeShapeButtonStyles = {
  ...shapeButtonStyles,
  bgcolor: 'action.selected',
  color: 'common.black',
};
const formControlLabelStyles = {
  m: 0,
  minHeight: 34,
  '& .MuiFormControlLabel-label': { fontSize: 13 },
};
const toggleLabelStyles = { alignItems: 'center' };
const toggleInfoStyles = { color: 'text.secondary', fontSize: 15 };
const newDefinitionButtonStyles = {
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
};
const shapeAnchorOrigin = { vertical: 'bottom', horizontal: 'left' } as const;
const errorStyles = { color: 'error.main' };
const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const shapeMessages = defineMessages({
  circle: { id: 'visualizer.shape.circle', defaultMessage: 'Circle' },
  square: { id: 'visualizer.shape.square', defaultMessage: 'Square' },
  triangle: { id: 'visualizer.shape.triangle', defaultMessage: 'Triangle' },
  rhombus: { id: 'visualizer.shape.rhombus', defaultMessage: 'Rhombus' },
  star: { id: 'visualizer.shape.star', defaultMessage: 'Star' },
});

function createDraftDefinition(): VisualizationDefinition {
  return {
    id: 'new-definition',
    filter: { ...EMPTY_FILTER },
    style: { ...DEFAULT_STYLE },
  };
}

function hasDefinitionFilter(filter: VisualizationDefinitionFilter): boolean {
  return Object.values(filter).some(
    (value) => Array.isArray(value) && value.length > 0
  );
}

function serializePreset(name: string, definitions: VisualizationDefinition[]) {
  return JSON.stringify({ name, definitions });
}

function compareSites(option: SiteOption, value: SiteOption): boolean {
  return option.id === value.id;
}

interface ShapeOptionProps {
  shape: VisualizationShape;
  style: VisualizationStyle;
  selected: boolean;
  onSelect: (shape: VisualizationShape) => void;
}

const ShapeOption = React.memo(
  ({ shape, style, selected, onSelect }: ShapeOptionProps) => {
    const intl = useIntl();
    const handleSelect = () => onSelect(shape);
    const previewStyle = { ...style, shape };

    return (
      <ButtonBase
        onClick={handleSelect}
        sx={selected ? selectedShapeOptionStyles : shapeOptionStyles}
      >
        <Stack alignItems="center" gap={0.5}>
          <VisualizationSymbol style={previewStyle} maxSize={30} />
          <Typography fontSize={11}>
            {intl.formatMessage(shapeMessages[shape])}
          </Typography>
        </Stack>
      </ButtonBase>
    );
  }
);

interface DefinitionRowProps {
  definition: VisualizationDefinition;
  selected: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

const DefinitionRow = React.memo(
  ({ definition, selected, onSelect, onDelete }: DefinitionRowProps) => {
    const intl = useIntl();
    const handleSelect = () => onSelect(definition.id);
    const handleDelete = (event: React.MouseEvent) => {
      event.stopPropagation();
      onDelete(definition.id);
    };

    return (
      <TableRow
        hover
        selected={selected}
        onClick={handleSelect}
        sx={definitionRowStyles}
      >
        <TableCell sx={definitionIconCellStyles}>
          <Stack alignItems="center">
            <VisualizationSymbol style={definition.style} maxSize={20} />
          </Stack>
        </TableCell>
        <TableCell sx={definitionFilterCellStyles}>
          <VisualizationDefinitionSummary
            definition={definition}
            showIcon={false}
            compact
          />
        </TableCell>
        <TableCell align="right" width={60}>
          <DesignSystemIconButton
            aria-label={intl.formatMessage({
              id: 'visualizer.definition.delete',
              defaultMessage: 'Delete definition',
            })}
            density="compact"
            onClick={handleDelete}
            tone="danger"
            variant="ghost"
          >
            <DeleteOutlineIcon />
          </DesignSystemIconButton>
        </TableCell>
      </TableRow>
    );
  }
);

export const Visualizer = React.memo(() => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { applyFindVisualization } = useSettings();
  const [presetName, setPresetName] = useState('');
  const [savedPresetId, setSavedPresetId] = useState<string | null>(null);
  const [definitions, setDefinitions] = useState<VisualizationDefinition[]>([]);
  const [baseline, setBaseline] = useState(() => serializePreset('', []));
  const [selectedDefinitionId, setSelectedDefinitionId] = useState<
    string | null
  >(null);
  const [draft, setDraft] = useState<VisualizationDefinition>(
    createDraftDefinition
  );
  const [selectedSite, setSelectedSite] = useState<SiteOption>(NONE_SITE);
  const [hideOthers, setHideOthers] = useState(false);
  const [shapeAnchor, setShapeAnchor] = useState<HTMLElement | null>(null);
  const [
    presetDialogMode,
    setPresetDialogMode,
  ] = useState<FindPresetDialogMode | null>(null);
  const [deleteDefinitionId, setDeleteDefinitionId] = useState<string | null>(
    null
  );
  const [editorError, setEditorError] = useState<string | null>(null);
  const [showSavedStatus, setShowSavedStatus] = useState(false);

  const { data: sitesData } = useQuery(GET_MY_SITES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const { data: filterTypesData } = useQuery(GET_ALL_FILTER_TYPES_QUERY, {
    fetchPolicy: 'cache-first',
  });
  const { data: findsData, loading: findsLoading } = useQuery(
    GET_SITE_FINDS_QUERY,
    {
      variables: {
        siteId: selectedSite.id,
      },
      fetchPolicy: 'cache-and-network',
      skip: !selectedSite.id,
    }
  );

  const sites = useMemo<SiteOption[]>(
    () => [NONE_SITE, ...(sitesData?.getMySites || [])],
    [sitesData]
  );
  const metadata = useMemo(
    () => ({
      eras: filterTypesData?.getAllFilterTypes?.eras || [],
      findTypes: filterTypesData?.getAllFilterTypes?.findTypes || [],
    }),
    [filterTypesData]
  );
  const finds = useMemo(
    () =>
      (findsData?.getSiteFinds || [])
        .map(normalizeVisualizerFind)
        .filter((find) => find != null),
    [findsData]
  );
  const selectedDefinition = definitions.find(
    (definition) => definition.id === selectedDefinitionId
  );
  const draftBase = selectedDefinition || createDraftDefinition();
  const hasPendingDefinitionChanges =
    JSON.stringify(draft) !== JSON.stringify(draftBase);
  const dirty =
    serializePreset(presetName, definitions) !== baseline ||
    hasPendingDefinitionChanges;
  useUnsavedChangesGuard(
    dirty,
    intl.formatMessage({
      id: 'visualizer.unsaved.navigate',
      defaultMessage:
        'Do you really want to discard unsaved changes and navigate away?',
    })
  );

  const getSiteLabel = useCallback(
    (option: SiteOption) =>
      option.id === null
        ? intl.formatMessage({ id: 'general.none', defaultMessage: 'None' })
        : option.name,
    [intl]
  );
  const siteTextFieldProps = useMemo(
    () => ({
      placeholder: intl.formatMessage({
        id: 'visualizer.previewSite.placeholder',
        defaultMessage: 'Select a preview site',
      }),
      sx: neutralInputStyles,
    }),
    [intl]
  );
  useEffect(() => {
    if (dirty) {
      setShowSavedStatus(false);
    }
  }, [dirty]);
  const previewDefinitions = useMemo(() => {
    if (!hasDefinitionFilter(draft.filter)) {
      return definitions;
    }

    if (selectedDefinitionId) {
      return definitions.map((definition) =>
        definition.id === selectedDefinitionId ? draft : definition
      );
    }

    return [...definitions, draft];
  }, [definitions, draft, selectedDefinitionId]);

  const updateDraftStyle = useCallback((patch: Partial<VisualizationStyle>) => {
    setDraft((current) => ({
      ...current,
      style: { ...current.style, ...patch },
    }));
    setEditorError(null);
  }, []);

  const handlePresetNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setPresetName(event.target.value);
  const handleSiteChange = (
    _event: React.SyntheticEvent,
    value: SiteOption | null
  ) => setSelectedSite(value || NONE_SITE);
  const handleHideOthersChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setHideOthers(event.target.checked);
  const handlePrimaryColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => updateDraftStyle({ primaryColor: event.target.value });
  const handleSecondaryColorChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => updateDraftStyle({ secondaryColor: event.target.value });
  const handleAddSecondaryColor = () =>
    updateDraftStyle({ secondaryColor: '#ffffff' });
  const handleRemoveSecondaryColor = () =>
    updateDraftStyle({ secondaryColor: null });
  const handleSizeChange = (_event: Event, value: number | number[]) =>
    updateDraftStyle({ sizeScale: value as number });
  const handleOutlinedChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateDraftStyle({ outlined: event.target.checked });
  const handleFilterChange = (filter: VisualizationDefinitionFilter) => {
    setDraft((current) => ({ ...current, filter }));
    setEditorError(null);
  };
  const handleClearFilter = () => handleFilterChange({});
  const handleShapeOpen = (event: React.MouseEvent<HTMLElement>) =>
    setShapeAnchor(event.currentTarget);
  const handleShapeClose = () => setShapeAnchor(null);
  const handleShapeSelect = useCallback(
    (shape: VisualizationShape) => {
      updateDraftStyle({ shape });
      setShapeAnchor(null);
    },
    [updateDraftStyle]
  );

  const handleDefinitionSelect = useCallback(
    (id: string) => {
      const definition = definitions.find((item) => item.id === id);
      if (!definition) {
        return;
      }

      setSelectedDefinitionId(id);
      setDraft({
        ...definition,
        filter: { ...definition.filter },
        style: { ...definition.style },
      });
      setEditorError(null);
    },
    [definitions]
  );

  const handleDefinitionCommit = () => {
    if (!hasDefinitionFilter(draft.filter)) {
      setEditorError(
        intl.formatMessage({
          id: 'visualizer.definition.filterRequired',
          defaultMessage: 'Select at least one filter value.',
        })
      );
      return;
    }

    if (
      !HEX_COLOR.test(draft.style.primaryColor) ||
      (draft.style.secondaryColor &&
        !HEX_COLOR.test(draft.style.secondaryColor))
    ) {
      setEditorError(
        intl.formatMessage({
          id: 'visualizer.definition.invalidColor',
          defaultMessage: 'Use six-digit hex colors such as #2e7d32.',
        })
      );
      return;
    }

    if (selectedDefinitionId) {
      setDefinitions((current) =>
        current.map((definition) =>
          definition.id === selectedDefinitionId ? draft : definition
        )
      );
    } else {
      setDefinitions((current) => [...current, { ...draft, id: createId() }]);
    }

    setSelectedDefinitionId(null);
    setDraft(createDraftDefinition());
    setEditorError(null);
  };

  const handleNewDefinition = () => {
    setSelectedDefinitionId(null);
    setDraft(createDraftDefinition());
    setEditorError(null);
  };

  const handleDeleteRequest = useCallback(
    (id: string) => setDeleteDefinitionId(id),
    []
  );
  const handleDeleteCancel = () => setDeleteDefinitionId(null);
  const handleDeleteConfirm = () => {
    setDefinitions((current) =>
      current.filter((definition) => definition.id !== deleteDefinitionId)
    );
    if (selectedDefinitionId === deleteDefinitionId) {
      handleNewDefinition();
    }
    setDeleteDefinitionId(null);
  };

  const handlePresetDialogClose = () => setPresetDialogMode(null);
  const handleOpenLoad = () => setPresetDialogMode('load');
  const handleOpenSave = () => {
    if (hasPendingDefinitionChanges) {
      setEditorError(
        intl.formatMessage({
          id: 'visualizer.definition.pending',
          defaultMessage: 'Add or update the current definition before saving.',
        })
      );
      return;
    }

    setPresetDialogMode('save');
  };
  const handlePresetLoad = (preset: VisualizationPreset) => {
    if (
      dirty &&
      !window.confirm(
        intl.formatMessage({
          id: 'visualizer.unsaved.load',
          defaultMessage:
            'Do you really want to discard unsaved changes and load another preset?',
        })
      )
    ) {
      return;
    }

    setPresetName(preset.name);
    setSavedPresetId(preset.id);
    setDefinitions(preset.definitions);
    setBaseline(serializePreset(preset.name, preset.definitions));
    setShowSavedStatus(false);
    handleNewDefinition();
  };
  const handlePresetSave = (preset: VisualizationPreset) => {
    setPresetName(preset.name);
    setSavedPresetId(preset.id);
    setDefinitions(preset.definitions);
    setBaseline(serializePreset(preset.name, preset.definitions));
    setShowSavedStatus(true);
    handleNewDefinition();
  };

  const handleApplyToMap = () => {
    if (!savedPresetId || dirty) {
      return;
    }

    applyFindVisualization({
      id: savedPresetId,
      kind: 'preset',
      preset: {
        id: savedPresetId,
        name: presetName,
        definitions,
      },
    });
    navigate('/');
  };

  return (
    <Box sx={pageStyles}>
      <Helmet
        title={intl.formatMessage({
          id: 'visualizer.pageTitle',
          defaultMessage: 'Visualizer editor | Acolyte',
        })}
      />
      <Stack direction="row" alignItems="center" gap={1.5} sx={headerStyles}>
        <IconButton
          component={Link}
          to="/"
          size="small"
          sx={homeButtonStyles}
        >
          <Logo size={34} />
        </IconButton>
        <Typography component="h1" variant="h6">
          <FormattedMessage
            id="visualizer.title"
            defaultMessage="Visualizer editor"
          />
        </Typography>
        {dirty && (
          <Chip
            size="small"
            color="warning"
            label={intl.formatMessage({
              id: 'visualizer.unsaved',
              defaultMessage: 'Unsaved',
            })}
          />
        )}
        {!dirty && showSavedStatus && (
          <Chip
            size="small"
            color="success"
            label={intl.formatMessage({
              id: 'visualizer.saved',
              defaultMessage: 'Saved',
            })}
          />
        )}
        <DesignSystemButton
          disabled
          density="compact"
          endIcon={<ArrowForwardIcon />}
          sx={playgroundButtonStyles}
          tone="neutral"
        >
          <FormattedMessage
            id="visualizer.toPlayground"
            defaultMessage="To playground"
          />
        </DesignSystemButton>
      </Stack>
      <Box sx={layoutStyles}>
        <Paper sx={editorStyles}>
          <Stack gap={2.5}>
            <Stack gap={1.5}>
              <Typography fontWeight={600}>
                <FormattedMessage
                  id="visualizer.preset"
                  defaultMessage="Preset"
                />
              </Typography>
              <Stack
                direction={compactRowDirection}
                gap={1}
                alignItems={responsiveControlAlignItems}
              >
                <FormField sx={fieldGroupStyles}>
                  <FieldLabel htmlFor="visualizer-preset-name">
                    <FormattedMessage
                      id="visualizer.preset.name"
                      defaultMessage="Preset name"
                    />
                  </FieldLabel>
                  <SurfaceTextField
                    id="visualizer-preset-name"
                    fullWidth
                    surface="sunken"
                    placeholder={intl.formatMessage({
                      id: 'visualizer.preset.namePlaceholder',
                      defaultMessage: 'Name this visualization preset',
                    })}
                    value={presetName}
                    onChange={handlePresetNameChange}
                    inputProps={nameInputProps}
                    sx={neutralInputStyles}
                  />
                </FormField>
                <Stack direction="row" gap={0.5} sx={presetActionsStyles}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenLoad}
                    sx={presetActionStyles}
                  >
                    <FormattedMessage id="general.load" defaultMessage="Load" />
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!definitions.length}
                    onClick={handleOpenSave}
                    sx={presetActionStyles}
                  >
                    <FormattedMessage id="general.save" defaultMessage="Save" />
                  </Button>
                  {!!savedPresetId && !dirty && (
                    <DesignSystemButton
                      density="compact"
                      endIcon={<ArrowForwardIcon />}
                      onClick={handleApplyToMap}
                      tone="neutral"
                    >
                      <FormattedMessage
                        id="visualizer.applyToMap"
                        defaultMessage="Apply to map"
                      />
                    </DesignSystemButton>
                  )}
                </Stack>
              </Stack>
              <Stack
                direction={compactRowDirection}
                gap={1}
                alignItems={responsiveControlAlignItems}
              >
                <FormField sx={fieldGroupStyles}>
                  <FieldLabel htmlFor="visualizer-preview-site">
                    <FormattedMessage
                      id="visualizer.previewSite"
                      defaultMessage="Preview site"
                    />
                  </FieldLabel>
                  <SurfaceAutocomplete
                    id="visualizer-preview-site"
                    fullWidth
                    disableClearable
                    options={sites}
                    value={selectedSite}
                    getOptionLabel={getSiteLabel}
                    isOptionEqualToValue={compareSites}
                    onChange={handleSiteChange}
                    surface="sunken"
                    textFieldProps={siteTextFieldProps}
                  />
                </FormField>
              </Stack>
            </Stack>

            <Stack gap={1.5}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontWeight={600}>
                  {selectedDefinitionId
                    ? intl.formatMessage({
                        id: 'visualizer.definition.edit',
                        defaultMessage: 'Edit definition',
                      })
                    : intl.formatMessage({
                        id: 'visualizer.definition.new',
                        defaultMessage: 'New definition',
                      })}
                </Typography>
                {selectedDefinitionId && (
                  <Button
                    size="small"
                    onClick={handleNewDefinition}
                    sx={newDefinitionButtonStyles}
                  >
                    <FormattedMessage
                      id="visualizer.definition.new"
                      defaultMessage="New definition"
                    />
                  </Button>
                )}
              </Stack>
              <Stack direction="row" flexWrap="wrap" sx={styleControlRowStyles}>
                <Stack direction="row" sx={colorControlStyles}>
                  <Typography
                    component="label"
                    htmlFor="visualizer-primary-color"
                    sx={staticLabelStyles}
                  >
                    <FormattedMessage
                      id="visualizer.color"
                      defaultMessage="Color"
                    />
                  </Typography>
                  <Stack
                    direction="row"
                    gap={0.5}
                    alignItems="center"
                    sx={colorValueRowStyles}
                  >
                    <Box
                      component="input"
                      id="visualizer-primary-color"
                      type="color"
                      aria-label={intl.formatMessage({
                        id: 'visualizer.color.primary',
                        defaultMessage: 'Primary color',
                      })}
                      value={
                        HEX_COLOR.test(draft.style.primaryColor)
                          ? draft.style.primaryColor
                          : '#000000'
                      }
                      onChange={handlePrimaryColorChange}
                      sx={nativeColorStyles}
                    />
                    <SurfaceTextField
                      surface="sunken"
                      value={draft.style.primaryColor}
                      onChange={handlePrimaryColorChange}
                      inputProps={colorInputProps}
                      sx={primaryColorFieldStyles}
                    />
                    {draft.style.secondaryColor ? (
                      <>
                        <Box
                          component="input"
                          type="color"
                          aria-label={intl.formatMessage({
                            id: 'visualizer.color.secondary',
                            defaultMessage: 'Secondary color',
                          })}
                          value={
                            HEX_COLOR.test(draft.style.secondaryColor)
                              ? draft.style.secondaryColor
                              : '#ffffff'
                          }
                          onChange={handleSecondaryColorChange}
                          sx={nativeColorStyles}
                        />
                        <SurfaceTextField
                          surface="sunken"
                          value={draft.style.secondaryColor}
                          onChange={handleSecondaryColorChange}
                          inputProps={colorInputProps}
                          sx={secondaryColorFieldStyles}
                        />
                        <IconButton
                          size="small"
                          aria-label={intl.formatMessage({
                            id: 'visualizer.color.removeSecondary',
                            defaultMessage: 'Remove secondary color',
                          })}
                          onClick={handleRemoveSecondaryColor}
                          sx={neutralIconButtonStyles}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Tooltip
                        title={intl.formatMessage({
                          id: 'visualizer.color.addSecondaryHelp',
                          defaultMessage: 'Add a second color to the icon',
                        })}
                      >
                        <IconButton
                          size="small"
                          aria-label={intl.formatMessage({
                            id: 'visualizer.color.addSecondary',
                            defaultMessage: 'Add secondary color',
                          })}
                          onClick={handleAddSecondaryColor}
                          sx={neutralIconButtonStyles}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" gap={1} sx={sizeControlStyles}>
                  <Typography component="span" sx={sizeValueStyles}>
                    {draft.style.sizeScale.toFixed(1)}×
                  </Typography>
                  <Slider
                    min={1}
                    max={3}
                    step={0.1}
                    value={draft.style.sizeScale}
                    onChange={handleSizeChange}
                    aria-label={intl.formatMessage({
                      id: 'visualizer.pointSize',
                      defaultMessage: 'Point size',
                    })}
                    sx={neutralSliderStyles}
                  />
                </Stack>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleShapeOpen}
                  startIcon={
                    <VisualizationSymbol style={draft.style} maxSize={20} />
                  }
                  sx={shapeAnchor ? activeShapeButtonStyles : shapeButtonStyles}
                >
                  {intl.formatMessage(shapeMessages[draft.style.shape])}
                </Button>
                <Popover
                  open={!!shapeAnchor}
                  anchorEl={shapeAnchor}
                  onClose={handleShapeClose}
                  anchorOrigin={shapeAnchorOrigin}
                >
                  <Box sx={shapeGridStyles}>
                    {SHAPES.map((shape) => (
                      <ShapeOption
                        key={shape}
                        shape={shape}
                        style={draft.style}
                        selected={draft.style.shape === shape}
                        onSelect={handleShapeSelect}
                      />
                    ))}
                  </Box>
                </Popover>
                <SwitchLabel
                  control={
                    <Switch
                      checked={draft.style.outlined}
                      onChange={handleOutlinedChange}
                      tone="success"
                    />
                  }
                  label={intl.formatMessage({
                    id: 'visualizer.outlined',
                    defaultMessage: 'Outlined',
                  })}
                  sx={formControlLabelStyles}
                />
              </Stack>
              <FilterBuilder
                filter={draft.filter}
                onChange={handleFilterChange}
                onRemove={handleClearFilter}
                showEraFilter
                showFindTypeFilter
                showMaterialFilter
                showTagFilter
                showRemoveButton={false}
                compact
                neutralStyle
              />
              {editorError && (
                <Typography fontSize={13} sx={errorStyles}>
                  {editorError}
                </Typography>
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <SwitchLabel
                  control={
                    <Switch
                      checked={hideOthers}
                      onChange={handleHideOthersChange}
                      tone="info"
                    />
                  }
                  label={
                    <Stack direction="row" gap={0.5} sx={toggleLabelStyles}>
                      <Typography component="span" fontSize={13}>
                        <FormattedMessage
                          id="visualizer.hideDefaults"
                          defaultMessage="Hide defaults"
                        />
                      </Typography>
                      <Tooltip
                        title={intl.formatMessage({
                          id: 'visualizer.hideDefaultsHelp',
                          defaultMessage:
                            'Hide finds using the predefined appearance so you can focus on the definitions you are editing when the map is crowded.',
                        })}
                      >
                        <InfoOutlinedIcon sx={toggleInfoStyles} />
                      </Tooltip>
                    </Stack>
                  }
                  sx={formControlLabelStyles}
                />
                <Button variant="contained" onClick={handleDefinitionCommit}>
                  {selectedDefinitionId
                    ? intl.formatMessage({
                        id: 'visualizer.definition.update',
                        defaultMessage: 'Update definition',
                      })
                    : intl.formatMessage({
                        id: 'visualizer.definition.add',
                        defaultMessage: 'Add definition',
                      })}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={mapStyles}>
          <VisualizerMapView
            site={selectedSite.id ? selectedSite : null}
            finds={finds}
            definitions={previewDefinitions}
            metadata={metadata}
            hideOthers={hideOthers}
          />
          {findsLoading && selectedSite.id && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={mapLoadingStyles}
            >
              <Typography>
                <FormattedMessage
                  id="visualizer.loadingFinds"
                  defaultMessage="Loading site finds…"
                />
              </Typography>
            </Stack>
          )}
        </Paper>

        <TableContainer component={Paper} sx={tableStyles}>
          <Table size="small" sx={definitionTableStyles}>
            <TableHead>
              <TableRow>
                <TableCell align="center" width={64}>
                  <FormattedMessage
                    id="visualizer.table.icon"
                    defaultMessage="Icon"
                  />
                </TableCell>
                <TableCell>
                  <FormattedMessage
                    id="visualizer.table.filters"
                    defaultMessage="Definition filters"
                  />
                </TableCell>
                <TableCell align="right">
                  <FormattedMessage
                    id="general.actions"
                    defaultMessage="Actions"
                  />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {definitions.length ? (
                definitions.map((definition) => (
                  <DefinitionRow
                    key={definition.id}
                    definition={definition}
                    selected={definition.id === selectedDefinitionId}
                    onSelect={handleDefinitionSelect}
                    onDelete={handleDeleteRequest}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography color="text.secondary" py={2}>
                      <FormattedMessage
                        id="visualizer.definition.empty"
                        defaultMessage="Add a definition to begin this visualization preset."
                      />
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {presetDialogMode && (
        <VisualizationPresetDialog
          mode={presetDialogMode}
          open
          name={presetName}
          definitions={definitions}
          onClose={handlePresetDialogClose}
          onLoad={handlePresetLoad}
          onSave={handlePresetSave}
        />
      )}

      <Dialog open={!!deleteDefinitionId} onClose={handleDeleteCancel}>
        <DialogTitle>
          <FormattedMessage
            id="visualizer.definition.deleteTitle"
            defaultMessage="Delete visualization definition?"
          />
        </DialogTitle>
        <DialogContent>
          <FormattedMessage
            id="visualizer.definition.deleteDescription"
            defaultMessage="This removes the definition from the local preset. Save the preset to persist the change."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} sx={newDefinitionButtonStyles}>
            <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
          >
            <FormattedMessage id="general.delete" defaultMessage="Delete" />
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
